import type { CommsSession } from '@/api/Comms';

interface ProfileSessionEnvelope {
  leaseId: string;
  session: CommsSession;
}

export type ProfileSessionMessage =
  | { type: 'request'; requestId: string }
  | {
      type: 'offer';
      requestId: string;
      envelope: ProfileSessionEnvelope;
    }
  | { type: 'available'; envelope: ProfileSessionEnvelope }
  | { type: 'revoked'; leaseId: string };

type MessageListener = (event: { data: ProfileSessionMessage }) => void;

export interface ProfileSessionBroadcastChannel {
  postMessage(message: ProfileSessionMessage): void;
  addEventListener(type: 'message', listener: MessageListener): void;
  removeEventListener(type: 'message', listener: MessageListener): void;
  close(): void;
}

interface ProfileSessionCoordinatorOptions {
  channelFactory?: (name: string) => ProfileSessionBroadcastChannel | null;
  runExclusive?: <T>(operation: () => Promise<T>) => Promise<T>;
  randomUUID?: () => string;
  responseWaitMilliseconds?: number;
  setTimeout?: typeof globalThis.setTimeout;
  clearTimeout?: typeof globalThis.clearTimeout;
}

export interface ProfileSessionCoordinator {
  acquire(createSession: () => Promise<CommsSession>): Promise<CommsSession>;
  revoke(endSession: () => Promise<void>): Promise<void>;
  subscribeRevoked(listener: () => void): () => void;
  dispose(): void;
}

const channelName = 'gba.comms.session';
const lockName = 'gba.comms.session.create';

const defaultChannelFactory = (
  name: string,
): ProfileSessionBroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(name) as ProfileSessionBroadcastChannel;
};

const defaultRunExclusive = async <T>(
  name: string,
  operation: () => Promise<T>,
) => {
  const locks = (
    navigator as Navigator & {
      locks?: {
        request<T>(name: string, callback: () => Promise<T>): Promise<T>;
      };
    }
  ).locks;
  return locks ? locks.request(name, operation) : operation();
};

const defaultRandomUUID = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const createProfileSessionCoordinator = (
  subject: string,
  options: ProfileSessionCoordinatorOptions = {},
): ProfileSessionCoordinator => {
  const channelFactory = options.channelFactory ?? defaultChannelFactory;
  const runExclusive =
    options.runExclusive ??
    ((operation) => defaultRunExclusive(`${lockName}.${subject}`, operation));
  const randomUUID = options.randomUUID ?? defaultRandomUUID;
  const responseWaitMilliseconds = options.responseWaitMilliseconds ?? 50;
  const setTimeoutFn = options.setTimeout ?? globalThis.setTimeout;
  const clearTimeoutFn = options.clearTimeout ?? globalThis.clearTimeout;
  const channel = channelFactory(`${channelName}.${subject}`);
  const revokedListeners = new Set<() => void>();
  const pending = new Map<
    string,
    {
      resolve: (envelope: ProfileSessionEnvelope | null) => void;
      timeout: ReturnType<typeof setTimeoutFn>;
    }
  >();
  let current: ProfileSessionEnvelope | null = null;
  let disposed = false;

  const resolvePending = (
    requestId: string,
    envelope: ProfileSessionEnvelope | null,
  ) => {
    const waiter = pending.get(requestId);
    if (!waiter) return;
    clearTimeoutFn(waiter.timeout);
    pending.delete(requestId);
    waiter.resolve(envelope);
  };

  const resolveEveryPending = (envelope: ProfileSessionEnvelope) => {
    [...pending.keys()].forEach((requestId) =>
      resolvePending(requestId, envelope),
    );
  };

  const onMessage: MessageListener = ({ data }) => {
    if (data.type === 'request') {
      if (current)
        channel?.postMessage({
          type: 'offer',
          requestId: data.requestId,
          envelope: current,
        });
      return;
    }
    if (data.type === 'offer') {
      current = data.envelope;
      resolvePending(data.requestId, data.envelope);
      return;
    }
    if (data.type === 'available') {
      current = data.envelope;
      resolveEveryPending(data.envelope);
      return;
    }
    if (current?.leaseId !== data.leaseId) return;
    current = null;
    revokedListeners.forEach((listener) => listener());
  };

  channel?.addEventListener('message', onMessage);

  const requestExisting = () => {
    if (current) return Promise.resolve(current);
    if (!channel) return Promise.resolve(null);
    const requestId = randomUUID();
    return new Promise<ProfileSessionEnvelope | null>((resolve) => {
      const timeout = setTimeoutFn(() => {
        pending.delete(requestId);
        resolve(null);
      }, responseWaitMilliseconds);
      pending.set(requestId, { resolve, timeout });
      channel.postMessage({ type: 'request', requestId });
    });
  };

  const acquire = async (createSession: () => Promise<CommsSession>) => {
    if (disposed) throw new Error('Messaging session coordinator is disposed.');
    const existing = await requestExisting();
    if (existing) return existing.session;
    return runExclusive(async () => {
      const coordinated = await requestExisting();
      if (coordinated) return coordinated.session;
      const envelope = {
        leaseId: randomUUID(),
        session: await createSession(),
      };
      current = envelope;
      channel?.postMessage({ type: 'available', envelope });
      return envelope.session;
    });
  };

  const revoke = (endSession: () => Promise<void>) =>
    runExclusive(async () => {
      if (!current) return;
      const leaseId = current.leaseId;
      try {
        await endSession();
      } finally {
        if (current?.leaseId === leaseId) current = null;
        channel?.postMessage({ type: 'revoked', leaseId });
        revokedListeners.forEach((listener) => listener());
      }
    });

  const subscribeRevoked = (listener: () => void) => {
    revokedListeners.add(listener);
    return () => revokedListeners.delete(listener);
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    pending.forEach(({ resolve, timeout }) => {
      clearTimeoutFn(timeout);
      resolve(null);
    });
    pending.clear();
    channel?.removeEventListener('message', onMessage);
    channel?.close();
    current = null;
    revokedListeners.clear();
  };

  return { acquire, revoke, subscribeRevoked, dispose };
};
